/**
 * Strategy Configuration Registry
 *
 * Centralized configuration for all strategy types. This module provides:
 * - Type definitions for strategy configurations
 * - Individual strategy configs (scoutList, freshFaces, etc.)
 * - Plain object registry for looking up configs by type
 * - Helper functions for common operations
 *
 * Usage:
 * ```tsx
 * import {
 *   STRATEGY_CONFIGS,
 *   getStrategyConfig,
 *   getStrategyButtons,
 *   validateStrategyComplete
 * } from 'src/components/Strategy/config';
 *
 * // Get buttons for strategy type selection
 * const buttons = getStrategyButtons('target');
 *
 * // Get config for a specific type
 * const config = getStrategyConfig('SCOUT_LIST');
 *
 * // Validate strategy completion
 * const isComplete = validateStrategyComplete('SCOUT_LIST', context);
 * ```
 */

// Type exports
export type {
  StrategyConfig,
  StrategyConfigProps,
  StrategyMode,
  StrategyValidationContext,
  CreateStrategyPayload,
  UpdateStrategyPayload,
  StrategyFeatures,
} from './types';

// Registry and helper function exports
export {
  STRATEGY_CONFIGS,
  getStrategyConfig,
  hasStrategyConfig,
  getAllStrategyConfigs,
  getStrategyConfigsByMode,
  getStrategyButtons,
  validateStrategyProgress,
  validateStrategyComplete,
  getStrategyConfigComponent,
  getStrategyLabel,
  serializeStrategyForCreate,
  serializeStrategyForUpdate,
} from './strategyRegistry';

export type { StrategyButton } from './strategyRegistry';

// Individual strategy config exports (for direct access if needed)
export {
  scoutListConfig,
  freshFacesConfig,
  referralsConfig,
  notReferringConfig,
  patientJourneyConfig,
  treatingVolumeConfig,
} from './strategies';
